import type { RefObject } from 'react';
import React, { createRef, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import * as d3 from 'd3';
import { discontinuityRange, scaleDiscontinuous } from '@d3fc/d3fc-discontinuous-scale';
import { useI18n } from '@whizz/react-i18n';
import type { GroupType, HighlightedProps, ProgressDataType, StudentType } from '../../../pages/types';
import type {
  ChartDataType,
  ChartIconType,
  CustomScatterChartAxisType,
  DrawProps,
  ReportPageScatterChartQueueType,
  ReportPageScatterChartType,
  ScatterStudentIconModeType,
  ScatterStudentIconType,
} from '../types';
import d3Helpers from '../helpers/func';
import Loader from '../../loader/Loader';
import {
  CHARTS_ANIMATION_DURATION,
  EXCELLENT_BREAKING_VALUES,
  EXCESSIVE_BREAKING_VALUES,
  FIXED_MAX_VALUES,
  GOOD_BREAKING_VALUES,
  NO_DOTTED_LINE_CHARTS,
  SCATTER_CHART_COLORS,
  STUDENT_PROGRESS_AXIS_HEIGHT,
  STUDENT_PROGRESS_AXIS_OFFSET,
  STUDENT_PROGRESS_BREAK_HEIGHT,
  STUDENT_PROGRESS_BREAK_WIDTH,
  STUDENT_PROGRESS_CLASSES,
  STUDENT_PROGRESS_MARGINS,
  STUDENT_PROGRESS_MIN_HEIGHT,
  STUDENT_PROGRESS_SMILEY_SIZE,
  STUDENT_PROGRESS_THIN_AXIS_HEIGHT,
  STUDENT_PROGRESS_TICK_HEIGHT,
  STUDENT_PROGRESS_TICK_NUMBER,
  STUDENT_PROGRESS_TICK_PADDING,
  STUDENT_PROGRESS_TICK_WIDTH,
  STUDENT_PROGRESS_VERTICAL_LINE_WIDTH,
} from '../helpers/vars';

import { APP_THEME } from '../../../styles/theme/styled/theme';
import type { IconsType } from '../../../context/IconContext';
import type { NumberValue } from 'd3';
import { IconContext } from '../../../context/IconContext';
import { PdfContext } from '../../../context/PdfContext';
import { isEqual, sample } from 'lodash';
import { useGenderIcon } from '../../../hooks/useGenderIcon';
import CluedoSearch from '../../../assets/Cluedo-Nudges-Teachers-Magnify.svg';

type Props = {
  data: ChartDataType[];
  showOthersData: boolean;
  chartType: ReportPageScatterChartType;
  type?: ChartIconType;
  thinAxis?: boolean;
  customAxis?: CustomScatterChartAxisType;
  showGradientBackground?: boolean;
  labelTranslationId?: string | null;
  removeUnassessed?: boolean;
  showProgress?: boolean;
  play?: boolean;
  excellentBreaking?: number | null;
} & Pick<HighlightedProps, 'highlightedIds' | 'onElementClick'>;

const ANIMATION_CONFIG = {
  interval: 5, // How often a student blinks in seconds
  animationInterval: 30, // How often a random animation should play
};

const animations:Array<keyof Omit<ScatterStudentIconModeType, 'open' | 'close'>> = [ 'bounce', 'jump', 'snooze', 'skipping_rope', 'glasses', 'juggler' ];
const getRandomAnimation = () => animations[ animations.length * Math.random() << 0];

const StudentProgress = ({
  data,
  showOthersData,
  chartType,
  type = 'student',
  thinAxis = false,
  customAxis,
  showGradientBackground = false,
  highlightedIds,
  labelTranslationId = null,
  removeUnassessed = false,
  showProgress = false,
  play = true,
  excellentBreaking = null,
  onElementClick,
}: Props): JSX.Element => {
  const { translate } = useI18n();

  const icons = useContext(IconContext);
  const { isPdf } = useContext(PdfContext);
  const [ getGenderIcon ] = useGenderIcon();

  const containerRef: RefObject<HTMLDivElement> = createRef();

  const studentGroup = useRef<{ group: d3.Selection<SVGGElement, PreparedDataType, SVGGElement, undefined> | null, queuedElements: ReportPageScatterChartQueueType[], previousQueuedElements: ReportPageScatterChartQueueType[] | null, previousData: PreparedDataType[] | null, height: number, axisHeight: number, barAxisHeight: number }>({
    group: null,
    queuedElements: [],
    previousQueuedElements: null,
    previousData: null,
    height: 0,
    axisHeight: 0,
    barAxisHeight: 0,
  });

  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const animationCount = useRef(1);
  const clickedElement = useRef<number | null>(null);

  const prepareData = (
    data: ChartDataType[],
    showOthersData: boolean,
    filterByIds: number[] | null,
  ): PreparedDataType[] => {

    return data
      .map((element) => ({
        id: element.id,
        fullName: element.fullName,
        gender: 'student' === type ? (element as StudentType).gender : null,
        value: null == (element.chartsData as Record<ProgressDataType, number>)[chartType]
          ? NaN
          : +(element.chartsData as Record<ProgressDataType, number>)[chartType],
        progress: (showProgress && element.chartsData?.mathsAgeImprovement !== null) ? element.chartsData?.mathsAgeImprovement : null,
        isHighlighted: true === filterByIds?.includes(element.id) && showOthersData,
        assessed: 'student' === type ? ('lastAssessmentDate' in element ? !!element.lastAssessmentDate : false) : true,
        color: 'student' !== type ? (element as GroupType).color : null,
      }))
    ;
  };

  const getComputeSmileyPosition = (width: number): number => {
    return width / 2 - STUDENT_PROGRESS_SMILEY_SIZE / 2;
  };

  // Text to show at the svg tooltip after the number, can be refactored to satisfy all chart types
  const tooltipMeasureText = (chartType === 'initialAssessment') ? '%' : '';

  const draw = (
    data: ChartDataType[],
    { width, margin, labelTranslationId, thinAxis }: DrawProps,
  ): void => {
    // Our condition makes draw not to trigger with null icons (not loaded)
    const fetchedIcons = (icons as IconsType);

    let showBreakingValue = true;

    // Width/height of element:
    const element_size = d3Helpers.getElementSize(type);

    // Prepares container:
    const container = d3Helpers.prepareContainer(containerRef);

    // Prepares tooltip container:
    const tooltip = d3Helpers.prepareTooltip(container);

    // Makes map of objects:
    const formattedData = prepareData(data, showOthersData, highlightedIds);

    // Computes which data points are considered defined:
    const I = formattedData.filter((datum) => !isNaN(datum.value));

    // Computes domains:
    // This uses data and not formattedData in order to keep the same axis with/without showing all elements.
    let xDomain = d3.extent(
      data.map(
        (value) =>
          (value.chartsData as Record<ProgressDataType, number>)[chartType],
      ),
    ) as number[]; // Map over data to prevent the axis to be rebuilt

    // Checks if xDomain contains any forced max value - if current max value is below the forced one, updates domain:

    if (customAxis) {
      const tickValues = customAxis.threshold && customAxis.tickValuesOverThreshold && (xDomain[1] > customAxis.threshold) ? customAxis.tickValuesOverThreshold : customAxis.tickValues;
      const xMin = tickValues[0];
      const xMax = Math.max(xDomain[1], customAxis.max);
      xDomain = [ xMin, xMax ];
    } else if (
      FIXED_MAX_VALUES[chartType] &&
      xDomain[1] < (FIXED_MAX_VALUES[chartType] as number)
    ) {
      xDomain = [ xDomain[0], FIXED_MAX_VALUES[chartType] as number ];
    }

    // Constructs scale:
    let xScale = d3.scaleLinear().domain(xDomain).range([ 0, width ]).nice();
    //.clamp(true)
    // Values for axis break:
    const [ breaks, first, next ] = d3Helpers.axisShouldBreak(
      d3.map(
        data,
        (element) =>
          (element.chartsData as Record<ProgressDataType, number>)[chartType],
      ),
    );

    const shouldHaveBreaks = customAxis ? false : breaks;
    // Prevent negative values from creating discontinuous scales
    if (shouldHaveBreaks && xDomain[0] >= 0) {
      // Reconstructs scale if the axis should break.
      // This uses https://yarnpkg.com/package/@d3fc/d3fc-discontinuous-scale
      xScale = scaleDiscontinuous(d3.scaleLinear())
        .discontinuityProvider(
          discontinuityRange([ (first as number) + 0.5, next ]),
        )
        .domain(xDomain)
        .range([ 0, width ])
        .nice();
    }

    // Constructs axis:
    const xAxis = d3
      .axisBottom(xScale)
      .tickSizeOuter(0)
      .tickPadding(STUDENT_PROGRESS_TICK_PADDING)
      .tickSize(STUDENT_PROGRESS_TICK_HEIGHT)
      .tickFormat((x) => {
        // Necessary to plot scales where max and min of the domain differ slightly
        const number = Math.abs(xDomain[0] - xDomain[1]) >= STUDENT_PROGRESS_TICK_NUMBER
          ? `${Math.floor(x as number)}`
          : `${x}`;
        return labelTranslationId !== null && (!customAxis?.highlight || !customAxis?.showUnitOnHighlightOnly || (customAxis?.showUnitOnHighlightOnly && String(customAxis?.highlight) === number))
          ? `${number} ${translate(`global.${labelTranslationId}`, {
            count: x as number,
          })}`
          : number;
      });

    if (customAxis) {
      if (customAxis.threshold && customAxis.tickValuesOverThreshold && (xDomain[1] > customAxis.threshold)) {
        xAxis.tickValues(customAxis.tickValuesOverThreshold as Iterable<NumberValue>);
      } else {
        xAxis.tickValues(customAxis?.tickValues as Iterable<NumberValue>);
      }
    } else {
      xAxis.ticks(STUDENT_PROGRESS_TICK_NUMBER);
    }

    I.reverse();

    I.sort((a, b) => {
      return (
        d3.ascending(a.isHighlighted, b.isHighlighted) ||
        d3.ascending(a.value, b.value)
      );
    });

    const sameData = studentGroup.current.previousData ? isEqual(I, studentGroup.current.previousData) : false;
    const queuedElements = studentGroup.current.queuedElements = (sameData && studentGroup.current.previousQueuedElements) ? studentGroup.current.previousQueuedElements : d3Helpers.queue(
      I.map(
        (element, index) =>
          ({
            x: xScale(element.value),
            y: null,
            id: element.id,
            index: index,
            isHighlighted: element.isHighlighted,
            title: element.fullName,
            value: element.value,
          } as ReportPageScatterChartQueueType),
      ),
      element_size,
      type === 'year' || type === 'class',
    );

    studentGroup.current.previousData = I;

    const showSmileys = !(thinAxis || showGradientBackground);
    // bar axis height (just the bar):
    const barAxisHeight = studentGroup.current.barAxisHeight = thinAxis ? STUDENT_PROGRESS_THIN_AXIS_HEIGHT : STUDENT_PROGRESS_AXIS_HEIGHT;

    const smileyHeight = !showSmileys ? 0 : STUDENT_PROGRESS_SMILEY_SIZE;

    const minHeight = !showSmileys ? 0 : STUDENT_PROGRESS_MIN_HEIGHT;

    // axis height (with all elements):
    const axisHeight = studentGroup.current.axisHeight =
      STUDENT_PROGRESS_TICK_PADDING * 2 + STUDENT_PROGRESS_TICK_HEIGHT;

    const excellentBreakingValue = (excellentBreaking ?? EXCELLENT_BREAKING_VALUES[chartType]);

    // svg height:
    let height: number;
    if (studentGroup.current.height) {
      height = studentGroup.current.height;
    } else {
      height = studentGroup.current.height =
      I.length > 0 ?
        ((d3.max(queuedElements.map((element) => element.y as number)) as number) +
      margin.top +
      margin.bottom +
      minHeight +
      element_size.highlighted_height +
      axisHeight +
      barAxisHeight +
      smileyHeight) : (STUDENT_PROGRESS_MIN_HEIGHT * 4);
    }

    // Returns negative width:
    const getNegativeWidth = () => {
      if (
        (getPositiveWidth() &&
          xDomain[0] >= (GOOD_BREAKING_VALUES[chartType] as number)) ||
        (getVeryPositiveWidth() &&
          xDomain[0] >= excellentBreakingValue)
      ) {
        return 0;
      } else if (!getPositiveWidth() && !getVeryPositiveWidth()) {
        return width;
      } else {
        return width - (getPositiveWidth() + getVeryPositiveWidth() + getExcessiveWidth());
      }
    };

    // Returns positive width:
    const getPositiveWidth = () => {
      if (GOOD_BREAKING_VALUES[chartType]) {
        const breakingAt = GOOD_BREAKING_VALUES[chartType] as number;
        if (xDomain[0] >= excellentBreakingValue) {
          return 0;
        } else if (xDomain[1] < breakingAt) {
          return 0;
        } else if (xDomain[0] === xDomain[1]) {
          return width;
        } else {
          return width - (xScale(breakingAt) + getVeryPositiveWidth() + getExcessiveWidth());
        }
      }

      return 0;
    };

    // Returns very positive/excellent width:
    const getVeryPositiveWidth = () => {
      if (xDomain[1] <= excellentBreakingValue) {
        return 0;
      } else if (xDomain[0] >= excellentBreakingValue) {
        return width;
      } else {
        return width - (xScale(excellentBreakingValue) + getExcessiveWidth());
      }
    };

    const getExcessiveWidth = () => {
      if (EXCESSIVE_BREAKING_VALUES[chartType]) {
        const breakingAt = EXCESSIVE_BREAKING_VALUES[chartType] as number;
        if (xDomain[1] <= breakingAt) {
          return 0;
        } else if (xDomain[0] === xDomain[1]) {
          return width;
        } else {
          return width - xScale(breakingAt);
        }
      }

      return 0;
    };

    // Main svg:
    const svg = d3
      .create('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('style', 'max-width: 100%; height: auto;');

    // Handles 0 elements selected + hide others = true
    if (!I.length) {
      svg
        .append('text')
        .text(`${translate('global.CHARTS.NO_DATA')}`)
        .attr('x', '50%')
        .attr('y', '50%')
        .attr('alignment-baseline', 'middle')
        .attr('text-anchor', 'middle')
        .style('font-style', 'italic');

      // Appends content into the dom:
      container.node()?.appendChild(svg.node() as Node);
      return;
    }

    if (onElementClick) {
      svg.classed(STUDENT_PROGRESS_CLASSES.cursor, true);
    }

    // Background
    if (showGradientBackground && getVeryPositiveWidth()) {
      const backgroundHeight = height - margin.top - margin.bottom - axisHeight;
      const startOfPositive = 100 - ((getVeryPositiveWidth() / width) * 100);

      const gradient = svg.append('defs').append('linearGradient');
      gradient.attr('id', 'gradientBg');
      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', SCATTER_CHART_COLORS.negative);

      gradient.append('stop')
        .attr('offset', `${startOfPositive}%`)
        .attr('stop-color', SCATTER_CHART_COLORS.positive);

      const gradientBackground = svg
        .append('g');

      gradientBackground
        .append('rect')
        .attr('width', width + (STUDENT_PROGRESS_AXIS_OFFSET * 2))
        .attr('height', backgroundHeight)
        .attr('opacity', 0.2)
        .attr('transform', `translate(${-STUDENT_PROGRESS_AXIS_OFFSET})`)
        .style('fill', 'url(#gradientBg');

      // Draw vertical green line
      gradientBackground
        .append('rect')
        .attr('width', STUDENT_PROGRESS_VERTICAL_LINE_WIDTH)
        .attr('height', backgroundHeight)
        .attr('transform', `translate(${((width / 100) * startOfPositive) - (STUDENT_PROGRESS_VERTICAL_LINE_WIDTH * 0.5)})`)
        .style('fill', SCATTER_CHART_COLORS.positive);
    }

    // xAxis group:
    const mainAxis = svg
      .append('g')
      .attr('transform', `translate(0, ${height - 50 - (STUDENT_PROGRESS_TICK_HEIGHT / 1.5)})`)
      .call(xAxis);

    // Adds width to ticks:
    mainAxis
      .selectAll('line')
      .attr('stroke-width', STUDENT_PROGRESS_TICK_WIDTH)
      .attr('stroke-linecap', 'round');

    // Removes default x-axis line:
    mainAxis.select('.domain').remove();

    // Counter for number of ticks.
    let tickCount = 0;

    // Checks if it should print breaking value line:
    mainAxis
      .selectAll('.tick>text')
      .style('font-size', '14px')
      .nodes()
      .map((text) => {
        tickCount++;
        showBreakingValue = ((text as SVGElement).innerHTML !== `${excellentBreakingValue}`);
      });

    if (customAxis?.highlight) {
      mainAxis
        .selectAll('.tick>text')
        .filter((d) => !!customAxis?.highlight?.includes(d))
        .style('font-weight', 'bold')
        .style('font-size', '16px');
    }

    if (showBreakingValue && tickCount > 1) {
      const breakingLine = svg
        .append('g')
        .attr(
          'transform',
          `translate(${xScale(excellentBreakingValue)}, 0)`,
        );

      // No dotted lines charts
      if (!NO_DOTTED_LINE_CHARTS.includes(chartType)) {
        breakingLine
          .append('line')
          .call((line) =>
            d3Helpers.prepareAverageLine(
              line,
              APP_THEME.colors.muted,
              height - axisHeight - barAxisHeight,
              0,
              0,
              2,
            ),
          );

        breakingLine
          .append('text')
          .text(chartType === 'mathsAgeImprovement' ? d3.format('.1f')(excellentBreakingValue) : excellentBreakingValue )
          .style('font-size', 10)
          .attr('y', 10)
          .attr('x', 5);
      }
    }

    if (showSmileys) {
      // Smileys at top:
      const smileys = svg.append('g').attr('transform', 'translate(0, 0)');

      // Positive smiley:
      if (getPositiveWidth()) {
        smileys
          .append('g')
          .attr(
            'transform',
            `translate(${
              getComputeSmileyPosition(getPositiveWidth()) + getNegativeWidth()
            }, ${STUDENT_PROGRESS_SMILEY_SIZE})`,
          )
          .call((group) => {
            group.each(function (this) {
              this.appendChild(fetchedIcons.smileyIcons.good.cloneNode(true));
            });
          });
      }

      // Very positive smiley:
      if (getVeryPositiveWidth()) {
        smileys
          .append('g')
          .attr(
            'transform',
            `translate(${
              getComputeSmileyPosition(getVeryPositiveWidth()) +
              (getNegativeWidth() + getPositiveWidth())
            }, ${STUDENT_PROGRESS_SMILEY_SIZE})`,
          )
          .call((group) => {
            group.each(function (this) {
              this.appendChild(fetchedIcons.smileyIcons.excellent.cloneNode(true));
            });
          });
      }

      // Excessive smiley:
      if (getExcessiveWidth()) {
        smileys
          .append('g')
          .attr(
            'transform',
            `translate(${
              getComputeSmileyPosition(getExcessiveWidth()) +
              (getNegativeWidth() + getPositiveWidth() + getVeryPositiveWidth())
            }, ${STUDENT_PROGRESS_SMILEY_SIZE})`,
          )
          .call((group) => {
            group.each(function (this) {
              this.appendChild(fetchedIcons.smileyIcons.good.cloneNode(true));
            });
          });
      }
    }

    // Colors at axis:
    const axisColors = svg
      .append('g')
      .attr(
        'transform',
        `translate(0, ${(height - axisHeight - STUDENT_PROGRESS_TICK_HEIGHT) + (thinAxis ? STUDENT_PROGRESS_THIN_AXIS_HEIGHT : 0)})`,
      );

    // Axis offset at left:
    axisColors
      .append('path')
      .attr('d', `M 0 0 L -${STUDENT_PROGRESS_AXIS_OFFSET  - 1} 0 C -${STUDENT_PROGRESS_AXIS_OFFSET + 3} 2 -${STUDENT_PROGRESS_AXIS_OFFSET + 3} ${barAxisHeight - 2} -${STUDENT_PROGRESS_AXIS_OFFSET - 1} ${barAxisHeight} L 0 ${barAxisHeight} L 0 0 Z`)
      .style(
        'fill',
        thinAxis
          ? SCATTER_CHART_COLORS.zero :
          getNegativeWidth()
            ? SCATTER_CHART_COLORS.negative
            : getPositiveWidth()
              ? SCATTER_CHART_COLORS.good
              : SCATTER_CHART_COLORS.positive,
      );

    // Axis offset at right:
    axisColors
      .append('path')
      .attr('d', `M ${width} 0 L ${STUDENT_PROGRESS_AXIS_OFFSET + width} 0 C ${width + (STUDENT_PROGRESS_AXIS_OFFSET + 3)} 2 ${width + (STUDENT_PROGRESS_AXIS_OFFSET + 3)} ${barAxisHeight - 2} ${width + STUDENT_PROGRESS_AXIS_OFFSET} ${barAxisHeight} L ${width} ${barAxisHeight} L ${width} 0 Z`)
      .style(
        'fill',
        thinAxis
          ? SCATTER_CHART_COLORS.zero :
          getExcessiveWidth()
            ? SCATTER_CHART_COLORS.good
            : getVeryPositiveWidth()
              ? SCATTER_CHART_COLORS.positive
              : getPositiveWidth()
                ? SCATTER_CHART_COLORS.good
                : SCATTER_CHART_COLORS.negative,
      );

    if (thinAxis) {
      axisColors
        .append('rect')
        .attr('width', width)
        .attr('height', barAxisHeight)
        .style('fill', SCATTER_CHART_COLORS.zero);
    } else {
      // Negative:
      if (getNegativeWidth()) {
        axisColors
          .append('rect')
          .attr('width', getNegativeWidth())
          .attr('height', barAxisHeight)
          .style('fill', SCATTER_CHART_COLORS.negative);
      }

      // Positive:
      if (getPositiveWidth()) {
        axisColors
          .append('rect')
          .attr('width', getPositiveWidth())
          .attr('height', barAxisHeight)
          .attr('x', getNegativeWidth())
          .style('fill', SCATTER_CHART_COLORS.good);
      }

      // Very positive:
      if (getVeryPositiveWidth()) {
        axisColors
          .append('rect')
          .attr('width', getVeryPositiveWidth())
          .attr('height', barAxisHeight)
          .attr('x', getNegativeWidth() + getPositiveWidth())
          .style('fill', SCATTER_CHART_COLORS.positive);
      }

      // Excessive:
      if (getExcessiveWidth()) {
        axisColors
          .append('rect')
          .attr('width', getExcessiveWidth())
          .attr('height', barAxisHeight)
          .attr('x', getNegativeWidth() + getPositiveWidth() + getVeryPositiveWidth())
          .style('fill', SCATTER_CHART_COLORS.good);
      }
    }

    // Prints little lines on the axis if it breaks:
    if (shouldHaveBreaks) {
      svg
        .append('g')
        .style('fill', SCATTER_CHART_COLORS.zero)
        .attr(
          'transform',
          `translate(${xScale(0.25)},
        ${
  height -
            axisHeight -
            STUDENT_PROGRESS_TICK_HEIGHT -
            barAxisHeight / 2
}) rotate(35 ${STUDENT_PROGRESS_BREAK_WIDTH} ${STUDENT_PROGRESS_BREAK_HEIGHT})`,
        )
        .selectAll('rect')
        .data([ 0, 1 ])
        .enter()
        .append('rect')
        .attr('height', STUDENT_PROGRESS_BREAK_HEIGHT)
        .attr('width', STUDENT_PROGRESS_BREAK_WIDTH)
        .attr('x', (index) => index * (STUDENT_PROGRESS_BREAK_WIDTH * 1.5));
    }

    // Creates a group of groups containing all single elements:
    const group = studentGroup.current.group = svg
      .append('g')
      .selectAll(`.${STUDENT_PROGRESS_CLASSES.element}`)
      .data(I)
      .enter()
      .append('g')
      .classed(STUDENT_PROGRESS_CLASSES.element, true)
      .attr('transform', (element) => {
        const qElement = (studentGroup.current.previousQueuedElements || queuedElements).find(
          (qElement) => qElement.id === element.id,
        );
        const x = studentGroup.current.previousQueuedElements ? (qElement?.x ?? 0) : 0;
        const y = qElement?.y ?? 0;
        const element_height = qElement?.isHighlighted ? element_size.highlighted_height : element_size.element_height;
        return `translate(${x}, ${
          height -
          margin.bottom -
          (axisHeight + barAxisHeight + element_height) -
          y
        })`;
      })
      .attr(STUDENT_PROGRESS_CLASSES.icon, (element) => element.fullName)
      .attr(STUDENT_PROGRESS_CLASSES.value, (element) => element.value)
      .sort((a, b) => {
        const findEl = (id: number) => id ? queuedElements.find(
          (qElement) => qElement.id === id,
        ) : null;

        if (a?.isHighlighted) {
          return 2;
        }

        if (a?.id && b?.id) {
          const aElem = findEl(a?.id);
          const bElem = findEl(b?.id);

          if (typeof aElem?.y === 'number' && typeof bElem?.y === 'number') {
            return bElem.y - aElem.y;
          }
        }
        return 0;
      });

    function printStudentElements(icons: ScatterStudentIconType, animate = false): void {
      // If it's time to play an animation only apply it students with values greater than zero. Otherwise let any student blink.
      let randomAnimation: typeof animations[number] | null = null;
      const filterStudentsWithValues = formattedData.filter((student) => student.value > 0);
      if (filterStudentsWithValues.length && animationCount.current++ % (ANIMATION_CONFIG.animationInterval / ANIMATION_CONFIG.interval) === 0) {
        animationCount.current = 1;
        randomAnimation = getRandomAnimation();
      }
      const randomId = sample(randomAnimation ? filterStudentsWithValues : formattedData)?.id;
      group.each(function (this, datum) {
        if (datum.assessed || !removeUnassessed) {
          const elementData = datum;

          const color = getGenderIcon(elementData.isHighlighted
            ? 'primary'
            : elementData.gender === 3
              ? 'other'
              : elementData.gender === 2
                ? 'female'
                : 'male') as keyof ScatterStudentIconType;

          const element = d3.select(this);
          element.selectAll('*').remove();

          if (!isPdf && animate && elementData.id === randomId) {
            if (randomAnimation) {
              this.appendChild(icons[color][randomAnimation].cloneNode(true));
            } else {
              this.appendChild(icons[color].close.cloneNode(true));
              setTimeout(() => {
                element.selectAll('*').remove();
                this.appendChild(icons[color].open.cloneNode(true));
              }, 200);
            }
          } else {
            this.appendChild(icons[color].open.cloneNode(true));
          }
        }
      });
    }

    // Student icons:
    if (type === 'student') {
      printStudentElements(fetchedIcons.studentIcons);
      if (!isPdf) {
        if (intervalId.current) {
          clearInterval(intervalId.current);
        }
        intervalId.current = setInterval(() => printStudentElements(fetchedIcons.studentIcons, true), ANIMATION_CONFIG.interval * 1000); // A student blinks every 5 seconds
      }
    }

    // Class icons:
    if (type === 'class') {
      group.each(function (this, datum) {
        const elementData = datum;
        const color = elementData.color ??// Todo Color code them based on color setting in Roster Management (RM). Blocked by RM release.
          (SCATTER_CHART_COLORS[
            elementData.isHighlighted
              ? 'highlight'
              : elementData.value >= excellentBreakingValue
                ? 'positive'
                : 'negative'
          ]);

        const element = d3.select(this);
        element.selectAll('*').remove();

        d3Helpers.scatterLabel((showOthersData && highlightedIds.length > 0), elementData.isHighlighted, elementData.fullName.substring(0, 5), element, color, true);

      });
    }

    // Year icons:
    if (type === 'year') {
      group.each(function (this, datum) {
        const elementData = datum;
        const color = elementData.color ??
          (SCATTER_CHART_COLORS[
            elementData.isHighlighted
              ? 'highlight'
              : elementData.value >= excellentBreakingValue
                ? 'positive'
                : 'negative'
          ]);
        const element = d3.select(this);
        element.selectAll('*').remove();

        d3Helpers.scatterLabel((showOthersData && highlightedIds.length > 0), elementData.isHighlighted, elementData.fullName, element, color);

      });
    }

    // Reposition markers
    group
      .selectAll(`.${STUDENT_PROGRESS_CLASSES.element} .marker`)
      .attr('cy', (element: any) => {
        const qElement = queuedElements.find((qElement) => qElement.id === (element as PreparedDataType)?.id);
        const y = qElement?.y ?? 0;
        const element_height = qElement?.isHighlighted ? element_size.highlighted_height : element_size.element_height;
        return element_height + margin.bottom - (STUDENT_PROGRESS_THIN_AXIS_HEIGHT / (thinAxis ? 0.75 : 1)) + y;
      });

    // Tooltip control:
    if (!isPdf) {

      const fade = (element: any, direction: 'in' | 'out', immediate = false) => {
        if (!immediate) {
          element
            .transition()
            .duration(750)
            .attr('opacity', direction === 'in' ? 1 : 0)
            .attr('disabled', direction === 'out' ? true : null);
          return;
        }
        element
          .attr('opacity', direction === 'in' ? 1 : 0)
          .attr('disabled', direction === 'out' ? true : null);
      };

      group.each(function (this, datum) {
        const element = d3.select(this);
        if (highlightedIds?.length) {
          if (highlightedIds.includes(datum.id)) {
            fade(element, 'in', clickedElement.current === datum.id);
          } else if (!showOthersData) {
            element.attr('opacity', 1);
            fade(element, 'out');
          }
        } else {
          fade(element, 'in', clickedElement.current === datum.id);
        }
      });

      const printTooltip = (datum: PreparedDataType, y: number) => {
        d3Helpers.printTooltipText(
          tooltip,
          datum.fullName,
          `${datum.value}${tooltipMeasureText}${typeof datum.progress === 'number' ? ` (${datum.progress.toFixed(2)})` : ''}`,
        );
        d3Helpers.moveTooltip(
          tooltip,
          width,
          xScale(datum.value),
          y,
        );
      };

      group
        .on('mouseover click', (event, datum) => {
          if (event.type === 'click' && onElementClick) {
            clickedElement.current = datum.id;
            onElementClick(datum.id);
          } else {
            d3Helpers.toggleTooltip(tooltip);
            printTooltip(datum, event.layerY);
          }
        })
        .on('mouseleave', () => {
          d3Helpers.toggleTooltip(tooltip, false);
        })
        .on('mousemove', (event, datum) => {
          printTooltip(datum, event.layerY);
        });

      // For touching screens:
      svg
        .on('touchend', () => {
          d3Helpers.toggleTooltip(tooltip, false);
        })
        .on('touchstart touchmove', (event) => {
          d3Helpers.handleTouchTooltipMove(event, tooltip, width);
        });
    }
    // Appends content into the dom:
    container.node()?.appendChild(svg.node() as Node);
  };

  const animate = () => {
    const { group, height, queuedElements, axisHeight, barAxisHeight } = studentGroup.current;
    const margin = { ...STUDENT_PROGRESS_MARGINS };
    if (group) {
      const element_size = d3Helpers.getElementSize(type);
      group.call((g) => {
        g.transition()
          .delay(750)
          .duration(isPdf ? 0 : CHARTS_ANIMATION_DURATION)
          .attr('transform', (element) => {
            const qElement = queuedElements.find(
              (qElement) => qElement.id === element.id,
            );
            const x = qElement?.x;
            const y = qElement?.y ?? 0;
            const element_height = qElement?.isHighlighted ? element_size.highlighted_height : element_size.element_height;
            return `translate(${x}, ${
              height -
              margin.bottom -
              (axisHeight + barAxisHeight + element_height) -
              y
            })`;
          });
      });
      studentGroup.current.previousQueuedElements = [ ...queuedElements ];
    }
  };

  useEffect(() => {
    studentGroup.current.height = 0;
  }, [ data ]);

  useEffect(() => {
    const width = containerRef.current?.offsetWidth ?? 0;
    if (data.length > 0 && width > 0 && icons) {
      draw(data, {
        width: width,
        margin: { ...STUDENT_PROGRESS_MARGINS },
        labelTranslationId: labelTranslationId,
        thinAxis: thinAxis,
      });
      if (play) {
        animate();
      }
    }
  }, [ data, icons, highlightedIds, showOthersData ]);

  useLayoutEffect(() => {
    if (highlightedIds.length) {
      if (highlightedIds.length === 1) {
        clickedElement.current = highlightedIds[0];
      } else if (clickedElement.current && !highlightedIds.includes(clickedElement.current)) {
        clickedElement.current = null;
      }
    }
  }, [ highlightedIds ]);

  useEffect(() => {
    if (studentGroup.current.group && play) {
      animate();
    }
  }, [ play ]);

  const hasData = () => {
    if (!data || data.length === 0) {
      return false;
    }
    const mathsAge = data.reduce((prev, current) => prev += current.chartsData?.mathsAge ?? 0, 0);
    return mathsAge > 0;
  };

  return (
    <>
      {!isPdf ? (
        !hasData()
          ? (
            <div className={'chart my-md flex justify-center items-center'}>
              <img src={CluedoSearch} />
              <p className={'text-xl font-bold'}>{translate('global.CHARTS.NO_DATA')}</p>
            </div>)
          : <div ref={containerRef} className={'chart'}><Loader /></div>
      ) : null }
    </>
  );
};

export default StudentProgress;

interface PreparedDataType {
  id: number;
  fullName: string;
  gender?: number | null;
  value: number;
  progress?: number | null;
  isHighlighted: boolean;
  assessed?: boolean;
  color?: string | null;
}
